import { useMouseMove } from "@asup/context-menu";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
} from "react";

export type DrawSide = "left" | "right";
type HorizontalDrawerState = "open" | "closing" | "closed" | "placing" | "opening";

/** Minimum width in pixels enforced during resize operations. */
const MIN_HORIZONTAL_DRAWER_WIDTH_PX = 100;

/** Default localStorage key used when no id prop is supplied. */
const HORIZONTAL_DRAWER_DEFAULT_STORAGE_KEY = "horizontalDrawer-width";

export interface HorizontalDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  show: boolean;
  width?: string;
  side?: DrawSide;
  timeMs?: number;
  bodyLevel?: boolean;
  pushContent?: boolean;
  targetElement?: HTMLElement;
  /** When true, renders a drag handle that allows the user to resize the drawer width. */
  resizable?: boolean;
}

/**
 * A horizontally animated drawer that slides in/out from the side.
 * Uses a state machine pattern for consistent animation behavior.
 * Can work at the document body level to push content aside, or within a container.
 *
 * @param props - Destructured parameter object
 * @param props.children - The content to display inside the drawer
 * @param props.show - Whether the drawer should be visible (expanded)
 * @param props.width - The target width when visible (defaults to "600px")
 * @param props.side - The side from which the drawer slides ('left' or 'right', defaults to 'right')
 * @param props.timeMs - Animation duration in milliseconds for open/close transitions (defaults to 300)
 * @param props.bodyLevel - If true, drawer attaches to viewport with fixed positioning and can push content (defaults to false)
 * @param props.pushContent - If true when bodyLevel is enabled, pushes the target element content over instead of overlaying (defaults to true, only applies when bodyLevel is true)
 * @param props.targetElement - The element to push when drawer opens at body level (defaults to document.body, only applies when bodyLevel and pushContent are true)
 * @param props.style - Additional inline styles to apply to the drawer
 * @param props.resizable - When true, renders a drag handle that allows the user to resize the drawer width; persists the chosen width to localStorage keyed by the id prop or a default key
 * @returns A drawer with horizontal slide animation
 */
export const HorizontalDrawer = ({
  show,
  width = "600px",
  side = "right",
  timeMs = 300,
  bodyLevel = false,
  pushContent = true,
  targetElement,
  resizable = false,
  children,
  style,
  ...divProps
}: HorizontalDrawerProps): React.ReactElement | null => {
  const [_isPending, startTransition] = useTransition();
  const [state, setState] = useState<HorizontalDrawerState>(show ? "open" : "closed");
  const [lastShow, setLastShow] = useState<boolean>(show);

  // Parse numeric width value for margin calculations
  const defaultWidthPx = parseInt(width, 10);
  const widthUnit = width.replace(/\d+/g, "");

  // Internal width state for resizable mode, initialised from the width prop
  const [widthPx, setWidthPx] = useState<number>(defaultWidthPx);
  const widthPxRef = useRef<number>(widthPx);

  // Keep the ref current so resize callbacks always use the latest value
  useLayoutEffect(() => {
    widthPxRef.current = widthPx;
  }, [widthPx]);

  // Derive the localStorage key from the supplied id prop, falling back to a default
  const storageKey =
    divProps.id !== undefined
      ? `horizontalDrawer-${divProps.id}-width`
      : `${HORIZONTAL_DRAWER_DEFAULT_STORAGE_KEY}-${side}-${bodyLevel ? "body" : "container"}`;

  // Load persisted width from localStorage on mount when resizable
  useLayoutEffect(() => {
    if (resizable) {
      const stored = window.localStorage.getItem(storageKey);
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) {
          startTransition(() => setWidthPx(parsed));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs once on mount only

  // Effective width string used for rendering
  const effectiveWidth = resizable ? `${widthPx}${widthUnit}` : width;
  const effectiveWidthValue = parseInt(effectiveWidth, 10);

  useLayoutEffect(() => {
    if (show !== lastShow) {
      if (show) {
        startTransition(() => setState("placing"));
      } else {
        startTransition(() => setState("closing"));
      }
      startTransition(() => setLastShow(show));
    }
  }, [state, show, lastShow]);

  // Store timeout IDs to clear them on cleanup
  const timeoutsRef = useRef<number[]>([]);
  const targetRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (state === "placing") {
      const id = window.setTimeout(() => {
        setState("opening");
      }, 5);
      timeoutsRef.current.push(id);
    }
    if (state === "opening") {
      const id = window.setTimeout(() => {
        setState("open");
      }, timeMs);
      timeoutsRef.current.push(id);
    }
    if (state === "closing") {
      const id = window.setTimeout(() => {
        setState("closed");
      }, timeMs);
      timeoutsRef.current.push(id);
    }

    // Cleanup: clear all scheduled timeouts
    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
  }, [state, timeMs]);

  // Handle pushing content when drawer opens/closes (only when bodyLevel is enabled)
  useLayoutEffect(() => {
    if (!bodyLevel || !pushContent) {
      return;
    }

    // Store the target element in a ref to avoid ESLint warnings about modifying props
    targetRef.current = targetElement ?? document.body;
    const target = targetRef.current;
    const isOpen = ["open", "opening"].includes(state);
    const marginValue = isOpen ? `${effectiveWidthValue}${widthUnit}` : "0";
    const marginProperty = side === "left" ? "marginLeft" : "marginRight";

    // Apply margin with transition
    target.style.transition = `margin ${timeMs}ms ease-in-out`;
    target.style[marginProperty] = marginValue;

    // Cleanup function to remove styles when component unmounts
    return () => {
      target.style[marginProperty] = "";
      target.style.transition = "";
    };
  }, [state, bodyLevel, pushContent, side, targetElement, effectiveWidthValue, widthUnit, timeMs]);

  // Refs for direct DOM manipulation during resize to avoid React re-render overhead
  const outerDivRef = useRef<HTMLDivElement>(null);
  const innerDivRef = useRef<HTMLDivElement>(null);
  const bodyDivRef = useRef<HTMLDivElement>(null);

  const resizeSessionRef = useRef<{
    startX: number;
    startWidthPx: number;
    latestWidthPx: number;
    elements: HTMLDivElement[];
  } | null>(null);

  /**
   * Applies drawer width updates during drag by manipulating DOM style directly (no React state).
   * Dragging left expands a right-side drawer; dragging right expands a left-side drawer.
   *
   * @param e - Native mouse move event emitted on the window.
   */
  const handleResizeMouseMove = (e: React.MouseEvent<Element, MouseEvent>) => {
    const session = resizeSessionRef.current;
    /* istanbul ignore next -- listeners only attached during active resize */
    if (session === null) {
      return;
    }

    // Right-side drawer: handle is on the left edge; dragging left widens the drawer (invert delta).
    // Left-side drawer: handle is on the right edge; dragging right widens the drawer (positive delta).
    // TODO: Accurate review comment...
    // When bodyLevel + pushContent is enabled, the pushed target margin is derived from React state (effectiveWidthValue).
    // During a resize drag, the drawer width is updated via direct DOM writes, but the pushed margin is not updated until
    // mouse-up commits state. This can cause the drawer width and pushed content margin to get out of sync while dragging.
    const deltaMultiplier = side === "right" ? -1 : 1;
    const nextWidth = Math.max(
      MIN_HORIZONTAL_DRAWER_WIDTH_PX,
      Math.round(session.startWidthPx + deltaMultiplier * (e.clientX - session.startX)),
    );

    if (nextWidth === session.latestWidthPx) {
      return;
    }

    session.latestWidthPx = nextWidth;
    session.elements.forEach((el) => {
      el.style.width = `${nextWidth}px`;
    });
  };

  /**
   * Completes a drawer width drag, commits the final width to state, and persists it to localStorage.
   */
  const handleResizeMouseUp = useCallback(() => {
    const session = resizeSessionRef.current;
    /* istanbul ignore next -- listeners only attached during active resize */
    if (session === null) {
      return;
    }

    const finalWidth = session.latestWidthPx;
    resizeSessionRef.current = null;

    setWidthPx(finalWidth);
    window.localStorage.setItem(storageKey, String(finalWidth));
  }, [storageKey]);

  /**
   * Starts a drawer width drag from the resize handle.
   *
   * @param event - React mouse down event from the resize handle.
   */
  const handleResizeMouseDown = (event: React.MouseEvent<HTMLElement | SVGElement>) => {
    /* istanbul ignore else -- handle is only rendered when resizable is true */
    if (resizable) {
      event.preventDefault();
      const elements: HTMLDivElement[] = [];
      if (bodyDivRef.current !== null) {
        elements.push(bodyDivRef.current);
      }
      if (outerDivRef.current !== null) {
        elements.push(outerDivRef.current);
      }
      if (innerDivRef.current !== null) {
        elements.push(innerDivRef.current);
      }
      /* istanbul ignore else */
      if (elements.length > 0) {
        resizeSessionRef.current = {
          startX: event.clientX,
          startWidthPx: widthPxRef.current,
          latestWidthPx: widthPxRef.current,
          elements,
        };
      }
    }
  };

  /**
   * Clears any active resize session on unmount.
   */
  useEffect(() => {
    return () => {
      resizeSessionRef.current = null;
    };
  }, []);

  const { onMouseDown: onResizeHandleMouseDown } = useMouseMove({
    onMouseDown: handleResizeMouseDown,
    onMouseMove: handleResizeMouseMove,
    onMouseUp: handleResizeMouseUp,
  });

  // Resize handle style: a narrow strip at the inner edge of the drawer with a resize cursor
  const resizeHandleStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    [side === "right" ? "left" : "right"]: 0,
    width: "6px",
    height: "100%",
    cursor: "col-resize",
    zIndex: 1,
    userSelect: "none",
  };

  // For body-level drawers use transform-based sliding so height remains constant
  const bodyVisible = ["open", "opening"].includes(state);
  const translateX = bodyVisible ? "0%" : side === "right" ? "100%" : "-100%";

  return state === "closed" ? null : bodyLevel ? (
    <div
      {...divProps}
      ref={bodyDivRef}
      style={{
        ...style,
        width: effectiveWidth,
        transform: `translateX(${translateX})`,
        transition: `transform ${timeMs}ms ease-in-out`,
        willChange: "transform",
        overflowX: "hidden",
        overflowY: "auto",
        position: "fixed",
        top: 0,
        [side]: 0,
        height: "100vh",
        zIndex: 1000,
      }}
    >
      {resizable && (
        <div
          aria-label="Resize drawer"
          style={resizeHandleStyle}
          onMouseDown={onResizeHandleMouseDown}
        />
      )}
      {children}
    </div>
  ) : (
    <div
      ref={outerDivRef}
      style={{
        width: ["open", "opening"].includes(state) ? effectiveWidth : "0",
        transition: `width ${timeMs}ms ease-in-out`,
        overflowX: "hidden",
        overflowY: "auto",
        position: "relative",
        height: "100%",
        marginLeft: side === "right" ? "auto" : undefined,
        marginRight: side === "left" ? "auto" : undefined,
      }}
    >
      <div
        {...divProps}
        ref={innerDivRef}
        style={{
          ...style,
          width: effectiveWidth,
          position: "absolute",
          top: 0,
          [side === "right" ? "left" : "right"]: 0,
          height: "100%",
        }}
      >
        {resizable && (
          <div
            aria-label="Resize drawer"
            style={resizeHandleStyle}
            onMouseDown={onResizeHandleMouseDown}
          />
        )}
        {children}
      </div>
    </div>
  );
};

HorizontalDrawer.displayName = "HorizontalDrawer";
